import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy, KeyRound, Loader2, ShieldBan, ShieldCheck, UserPlus } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  createUser,
  getAdminUser,
  inviteUser,
  listAudit,
  listUsers,
  resetUserPassword,
  setUserDisabled,
  type AdminUser,
  type AdminUserDetail,
  type AuditEntry,
  type Role,
} from '@/lib/api-client';

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

export function Admin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [actionFilter, setActionFilter] = useState('');
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setUsers(await listUsers().catch(() => []));
  }, []);
  const refreshAudit = useCallback(async () => {
    setAudit(await listAudit({ action: actionFilter || undefined, limit: 100 }).catch(() => []));
  }, [actionFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    void refreshAudit();
  }, [refreshAudit]);

  async function toggleDisabled(u: AdminUser): Promise<void> {
    setError(null);
    try {
      await setUserDisabled(u.id, u.status !== 'DISABLED');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the user.');
    }
  }

  async function openDetail(id: string): Promise<void> {
    setError(null);
    try {
      setDetail(await getAdminUser(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load user detail.');
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Admin</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <InviteForm onInvited={() => void refresh()} />

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Users ({users.length})
          </p>
          <div className="overflow-hidden rounded-md border border-stone-800/60">
            <table className="w-full text-left text-sm">
              <thead className="bg-stone-900/60 text-[10px] uppercase tracking-[0.15em] text-stone-500">
                <tr>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2 text-right">Matters / Sessions</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-stone-800/60">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => void openDetail(u.id)}
                        className="text-left text-stone-200 hover:text-amber-400"
                      >
                        {u.name ?? u.email}
                        <span className="block text-[10px] text-stone-500">{u.email}</span>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-stone-400">{u.role}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="px-3 py-2 text-stone-400">{fmtDate(u.createdAt)}</td>
                    <td className="px-3 py-2 text-stone-400">{fmtDate(u.lastLoginAt)}</td>
                    <td className="px-3 py-2 text-right text-stone-400">
                      {u.matterCount} / {u.sessionCount}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setResetTarget(u)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400"
                        >
                          <KeyRound className="h-3.5 w-3.5" /> Password
                        </button>
                        <button
                          type="button"
                          onClick={() => void toggleDisabled(u)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400"
                        >
                          {u.status === 'DISABLED' ? (
                            <>
                              <ShieldCheck className="h-3.5 w-3.5" /> Enable
                            </>
                          ) : (
                            <>
                              <ShieldBan className="h-3.5 w-3.5" /> Disable
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-stone-500">
                      No users yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Audit log</p>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="rounded-md border border-stone-800 bg-stone-900 px-2 py-1 text-xs text-stone-300"
            >
              <option value="">All actions</option>
              {AUDIT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            {audit.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-3 rounded border border-stone-800/50 bg-stone-900/40 px-3 py-1.5 text-xs"
              >
                <span className="font-mono text-amber-300/80">{e.action}</span>
                <span className="truncate text-stone-500">{JSON.stringify(e.detail)}</span>
                <span className="shrink-0 text-stone-600">{fmtDateTime(e.createdAt)}</span>
              </div>
            ))}
            {audit.length === 0 ? <p className="text-sm text-stone-500">No audit entries.</p> : null}
          </div>
        </section>
      </div>

      {detail ? <UserDetailModal detail={detail} onClose={() => setDetail(null)} /> : null}
      {resetTarget ? (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => {
            setResetTarget(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('USER');
  // 'password': create an ACTIVE user with a password entered once here.
  // 'invite': the original single-use activation-link flow.
  const [mode, setMode] = useState<'password' | 'invite'>('password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activationUrl, setActivationUrl] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit(): Promise<void> {
    setError(null);
    setActivationUrl(null);
    setCreated(null);
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (mode === 'password') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirm) {
        setError('Passwords do not match.');
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === 'password') {
        const result = await createUser({
          email: email.trim(),
          name: name.trim() || undefined,
          role,
          password,
        });
        setCreated(result.user.email);
      } else {
        const result = await inviteUser({
          email: email.trim(),
          name: name.trim() || undefined,
          role,
        });
        setActivationUrl(result.activationUrl);
      }
      setEmail('');
      setName('');
      setRole('USER');
      setPassword('');
      setConfirm('');
      onInvited();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the user.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    if (!activationUrl) return;
    try {
      await navigator.clipboard.writeText(activationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked; the URL is still visible to select manually
    }
  }

  return (
    <Card variant="default" padding="md" className="space-y-4 border-amber-900/30 bg-amber-950/10">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Add a user</p>
        <div className="flex gap-1 rounded-md border border-stone-800 p-0.5 text-[10px] uppercase tracking-[0.15em]">
          <button
            type="button"
            onClick={() => setMode('password')}
            className={`rounded px-2 py-1 ${mode === 'password' ? 'bg-amber-600 text-stone-950' : 'text-stone-500 hover:text-stone-300'}`}
          >
            Set password
          </button>
          <button
            type="button"
            onClick={() => setMode('invite')}
            className={`rounded px-2 py-1 ${mode === 'invite' ? 'bg-amber-600 text-stone-950' : 'text-stone-500 hover:text-stone-300'}`}
          >
            Invitation link
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className={labelClass} htmlFor="inv-email">
            Email
          </label>
          <input
            id="inv-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="inv-name">
            Name
          </label>
          <input
            id="inv-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="inv-role">
            Role
          </label>
          <select
            id="inv-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className={inputClass}
          >
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>
      </div>
      {mode === 'password' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="inv-password">
              Password
            </label>
            <input
              id="inv-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="inv-confirm">
              Confirm password
            </label>
            <input
              id="inv-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat the password"
              className={inputClass}
            />
          </div>
        </div>
      ) : null}
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {mode === 'password' ? 'Create user' : 'Create invitation'}
        </button>
      </div>
      {created ? (
        <div className="rounded-md border border-emerald-900/40 bg-emerald-950/20 p-3">
          <p className="text-xs text-emerald-300">
            User {created} created and active. The password is not shown again — share it securely.
          </p>
        </div>
      ) : null}
      {activationUrl ? (
        <div className="space-y-2 rounded-md border border-emerald-900/40 bg-emerald-950/20 p-3">
          <p className="text-xs text-emerald-300">
            Invitation created. Send this activation link to the new user (no email is sent
            automatically):
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-stone-900 px-2 py-1.5 text-[11px] text-stone-300">
              {activationUrl}
            </code>
            <button
              type="button"
              onClick={() => void copy()}
              className="inline-flex shrink-0 items-center gap-1 rounded border border-stone-700 px-2 py-1.5 text-[11px] text-stone-300 hover:border-amber-700/60"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function UserDetailModal({ detail, onClose }: { detail: AdminUserDetail; onClose: () => void }) {
  const { user, audit } = detail;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Card
        variant="panel"
        padding="md"
        className="max-h-[80vh] w-full max-w-lg space-y-4 overflow-y-auto"
        // stop backdrop click from closing when interacting with the card
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg text-stone-100">{user.name ?? user.email}</h2>
            <p className="text-xs text-stone-500">{user.email}</p>
          </div>
          <StatusBadge status={user.status} />
        </div>
        <dl className="grid grid-cols-2 gap-2 text-xs text-stone-400">
          <Field label="Role" value={user.role} />
          <Field label="Matters / Sessions" value={`${user.matterCount} / ${user.sessionCount}`} />
          <Field label="Invited" value={fmtDate(user.invitedAt)} />
          <Field label="Activated" value={fmtDate(user.activatedAt)} />
          <Field label="Last login" value={fmtDate(user.lastLoginAt)} />
          <Field label="Invited by" value={user.invitedBy?.email ?? '—'} />
        </dl>
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Recent activity
          </p>
          <div className="space-y-1">
            {audit.length === 0 ? (
              <p className="text-sm text-stone-500">No activity yet.</p>
            ) : (
              audit.map((e) => (
                <div key={e.id} className="flex justify-between gap-2 text-xs">
                  <span className="font-mono text-amber-300/80">{e.action}</span>
                  <span className="shrink-0 text-stone-600">{fmtDateTime(e.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            Close
          </button>
        </div>
      </Card>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await resetUserPassword(user.id, password);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set the password.');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Card
        variant="panel"
        padding="md"
        className="w-full max-w-sm space-y-4"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg text-stone-100">Set a new password</h2>
          <p className="text-xs text-stone-500">
            {user.email} — entered once, never displayed again.
          </p>
        </div>
        <div className="space-y-3">
          <div>
            <label className={labelClass} htmlFor="reset-password">
              New password
            </label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reset-confirm">
              Confirm password
            </label>
            <input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat the password"
              className={inputClass}
            />
          </div>
        </div>
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Set password
          </button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.15em] text-stone-600">{label}</dt>
      <dd className="text-stone-300">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminUser['status'] }) {
  const styles: Record<AdminUser['status'], string> = {
    ACTIVE: 'border-emerald-700/50 bg-emerald-950/30 text-emerald-300',
    INVITED: 'border-amber-700/50 bg-amber-950/30 text-amber-300',
    DISABLED: 'border-stone-700 bg-stone-900 text-stone-500',
  };
  return (
    <span className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${styles[status]}`}>
      {status}
    </span>
  );
}

const AUDIT_ACTIONS = [
  'auth.login',
  'auth.login_failed',
  'admin.user.invite',
  'admin.user.create',
  'admin.user.password_reset',
  'admin.user.disable',
  'admin.user.enable',
  'user.activate',
  'matter.create',
  'session.export',
  'password.change',
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}
